/**
 * Bare preferences window, 1160×833. Figma `9229:191444` (Account)
 * plus later settings frames in the same file.
 *
 * The window is a frame (见 21): it draws the sidebar, the header and the pane,
 * and stacks the sections of the current page. What the pages hold comes from
 * `buildSettingsTree` — the platform's own pages plus every module's
 * `contributes.setting`. Nothing here knows a module id or calls a module
 * command other than an action row's declared `command`.
 *
 * Outer 14px round / drop-shadow / backdrop-blur are the OS-window mockup and
 * are not painted. Inner sidebar is 256×10px-radius module-container; chrome
 * is native traffic lights (mac, left of sidebar) or custom buttons (Win/Linux,
 * top-right of the right pane).
 *
 * Several inline views on one page share one loading indicator. The sections
 * stay mounted; iframe and external views keep their own indicators.
 *
 * The page title bar is the only drag region (`.settings-titlebar`). The
 * window root is not: a confirm sheet portaled onto it would swallow the
 * click and the window would move instead.
 */
import './motion.css';

import type { ChannelClient } from '@hibro/platform-core';
import type {
  IConfigurationService,
  IconSpec,
  ILauncherService,
  IModuleService,
  INlsService,
  IProductConfigService,
  IThemeService,
  IUpdateService,
  IWhenContextService,
  ResolvedActivityBarItem,
  ResolvedModuleSetting,
} from '@hibro/shared';
import {
  CONTRIBUTIONS_CHANGED_EVENT,
  parseSurfaceTarget,
  readSettingsPageId,
  SETTING_PAGE_ABOUT,
  SETTING_PAGE_NOTIFICATIONS,
  SETTINGS_HEADER_PROVIDER_KEY,
  SETTINGS_WINDOW_SELECT_PAGE_EVENT,
} from '@hibro/shared';
import {
  cn,
  Icon,
  SettingsConfirmSheet,
  SettingsHint,
  type SettingsLeaveGuard,
  SettingsPane,
  SettingsPaneChromeProvider,
  type SettingsPaneDrill,
  toast,
} from '@hibro/ui-client';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { OpenExternalBridge } from '../../app-links/open';
import type { WindowControlsBridge } from '../../components/custom-header/window-controls';
import { WindowControls } from '../../components/custom-header/window-controls';
import { useTranslator } from '../../i18n/i18n';
import { SurfaceLoadingIndicator } from '../../surfaces/surface-loading';
import { BuiltinSection } from './builtins';
import {
  ANCHOR_LAST_MIN_HEIGHT_VAR,
  AnchorSection,
  SIDEBAR_PRESS,
  SIDEBAR_SELECTED,
} from './chrome';
import {
  applyDeveloperTap,
  clearDeveloperEnabled,
  type DeveloperTapState,
  initialDeveloperTapState,
  readDeveloperEnabled,
  writeDeveloperEnabled,
} from './developer-unlock';
import { LanguageIcon } from './language-icon';
import { settingsWindowLogError, settingsWindowLogWarn } from './log';
import { NotificationsSettings } from './notifications-page';
import { PageViewLoadingProvider } from './page-view-loading';
import { SettingsSectionErrorBoundary } from './section-error';
import {
  KeysSection,
  type SettingsConfig,
  useSettingsConfig,
} from './setting-row';
import {
  buildSettingsTree,
  filterSettingsTree,
  findSettingsPage,
  firstSettingsPage,
  settingsPageOfSection,
  type SettingsTreeAction,
  type SettingsTreePage,
  type SettingsTreeSection,
} from './tree';

const MAC_TRAFFIC_LIGHT_SPACING = 72;

/** A section counts as current once its top is within this distance of the pane. */
const ANCHOR_ACTIVATE_OFFSET = 48;

/** Anchor row is py-2.5 (10px) plus a 22px line. The 16px tick sits 13px in. */
const ANCHOR_ROW_HEIGHT = 42;
const ANCHOR_TICK_TOP = (ANCHOR_ROW_HEIGHT - 16) / 2;

const DIVIDER =
  'h-px w-full shrink-0 bg-(--app-base-black-a4) dark:bg-(--app-base-white-a4)';

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function failureReason(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function scrollerPadding(root: HTMLElement): { top: number; bottom: number } {
  const style = window.getComputedStyle(root);
  return {
    top: Number.parseFloat(style.paddingTop),
    bottom: Number.parseFloat(style.paddingBottom),
  };
}

function readActiveAnchor(root: HTMLElement): string | null {
  const rootTop = root.getBoundingClientRect().top;
  let current: string | null = null;
  let first: string | null = null;
  for (const node of root.querySelectorAll('[data-settings-anchor]')) {
    if (!(node instanceof HTMLElement) || node.getClientRects().length === 0) {
      continue;
    }
    const id = node.dataset.settingsAnchor;
    if (!id) {
      continue;
    }
    first ??= id;
    if (node.getBoundingClientRect().top - rootTop <= ANCHOR_ACTIVATE_OFFSET) {
      current = id;
    }
  }
  return current ?? first;
}

export interface SettingsWindowProps {
  moduleService: IModuleService;
  windowControls?: WindowControlsBridge;
  configService?: IConfigurationService;
  client?: ChannelClient;
  nlsService?: INlsService;
  themeService?: IThemeService;
  /**
   * Host a module view (a `viewId` section or the identity header). The caller
   * supplies `SurfaceView`; this window only picks the view id.
   */
  renderModuleView?: (moduleId: string, viewId: string) => ReactNode;
  /**
   * Old platform settings body. Shown on the developer-options page after the
   * About mark is unlocked. The shell owns the bridges behind it.
   */
  developerSettings?: ReactNode;
  /** Running build and the manual check behind the About version row. */
  updateService?: IUpdateService;
  /** System browser for About's website, agreement, and privacy rows. */
  launcherService?: ILauncherService;
  /** `app.homepage` / `app.termsUrl` / `app.privacyUrl`, and the header pin. */
  productConfigService?: IProductConfigService;
  /**
   * Module `when` keys for the slash-command page. The same snapshot the
   * search box evaluates `enablement` against.
   */
  whenContextService?: IWhenContextService;
  /**
   * Web open that stays inside the click. When present it wins over
   * `launcherService`, same as the update prompt.
   */
  openExternalBridge?: OpenExternalBridge;
}

interface PaneDrill extends SettingsPaneDrill {
  readonly sectionId: string;
}

/** The action confirm sheet does not drill or guard the page. */
function ignorePaneDrill(_drill: SettingsPaneDrill | null): void {}

function ignoreLeaveGuard(_guard: SettingsLeaveGuard | null): void {}

export function SettingsWindow({
  moduleService,
  windowControls,
  configService,
  client,
  nlsService,
  themeService,
  renderModuleView,
  developerSettings,
  updateService,
  launcherService,
  productConfigService,
  openExternalBridge,
  whenContextService,
}: SettingsWindowProps): ReactNode {
  const { t, title } = useTranslator();
  const [query, setQuery] = useState('');
  const [pageId, setPageId] = useState<string | null>(() => {
    const target = parseSurfaceTarget(window.location.search);
    return readSettingsPageId(target?.payload) ?? null;
  });
  const [drill, setDrill] = useState<PaneDrill | null>(null);
  const [overlayRoot, setOverlayRoot] = useState<HTMLDivElement | null>(null);
  const [pendingAction, setPendingAction] = useState<SettingsTreeAction | null>(
    null,
  );
  const leaveGuards = useRef(new Map<string, SettingsLeaveGuard>());
  const [setting, setSetting] = useState<readonly ResolvedModuleSetting[]>([]);
  const [activityBar, setActivityBar] = useState<
    readonly Pick<ResolvedActivityBarItem, 'moduleId' | 'custom'>[]
  >([]);
  const [headerProvider, setHeaderProvider] = useState('');
  const [developerEnabled, setDeveloperEnabled] = useState(() =>
    readDeveloperEnabled(window.localStorage),
  );
  const developerTap = useRef<DeveloperTapState>(
    initialDeveloperTapState(developerEnabled),
  );
  const config = useSettingsConfig(configService, client, t);

  const refreshContributions = useCallback(() => {
    void moduleService
      .listContributions()
      .then((contributions) => {
        setSetting(contributions.setting);
        setActivityBar(contributions.activityBar);
      })
      .catch((err: unknown) => {
        settingsWindowLogError('settings window contributions load failed', {
          reason: failureReason(err),
        });
      });
  }, [moduleService]);

  useEffect(() => {
    refreshContributions();
  }, [refreshContributions]);

  useEffect(() => {
    if (!client) {
      return;
    }
    return client.listen(CONTRIBUTIONS_CHANGED_EVENT, () => {
      refreshContributions();
    });
  }, [client, refreshContributions]);

  useEffect(() => {
    if (!productConfigService) {
      return;
    }
    let active = true;
    void productConfigService
      .getValue(SETTINGS_HEADER_PROVIDER_KEY)
      .then((value) => {
        if (active) {
          setHeaderProvider(typeof value === 'string' ? value.trim() : '');
        }
      })
      .catch((err: unknown) => {
        settingsWindowLogError('settings window header provider load failed', {
          reason: failureReason(err),
        });
      });
    return () => {
      active = false;
    };
  }, [productConfigService]);

  const tree = useMemo(
    () =>
      buildSettingsTree({
        setting,
        activityBar,
        developerEnabled,
        headerProvider,
        platformLabel: (key) => t(key),
        moduleLabel: title,
      }),
    [setting, activityBar, developerEnabled, headerProvider, t, title],
  );

  useEffect(() => {
    if (tree.headerProblem) {
      settingsWindowLogWarn('settings window identity header not drawn', {
        reason: tree.headerProblem,
      });
    }
  }, [tree.headerProblem]);

  const keyLabel = useCallback(
    (_moduleId: string, key: string) => {
      const entry = config.schema.get(key);
      return entry?.title ? title(entry.moduleId, entry.title) : undefined;
    },
    [config.schema, title],
  );
  const shown = useMemo(
    () => filterSettingsTree(tree, query, keyLabel),
    [tree, query, keyLabel],
  );
  const searching = query.trim().length > 0;

  const page: SettingsTreePage | null =
    (pageId ? findSettingsPage(tree, pageId) : null) ?? firstSettingsPage(tree);

  /** Every section's guard in turn, then the navigation itself. */
  const leave = useCallback((proceed: () => void) => {
    const guards = [...leaveGuards.current.values()];
    const run = (index: number) => {
      const guard = guards[index];
      if (!guard) {
        proceed();
        return;
      }
      guard(() => run(index + 1));
    };
    run(0);
  }, []);

  const onAboutLogoTap = useCallback(() => {
    const next = applyDeveloperTap(developerTap.current, Date.now());
    developerTap.current = next.state;
    if (next.result.kind === 'already') {
      toast(t('settingsWindow.developer.already'));
      return;
    }
    if (next.result.kind === 'progress') {
      if (next.result.announce) {
        toast(
          t('settingsWindow.developer.steps', {
            count: next.result.remaining,
          }),
        );
      }
      return;
    }
    try {
      writeDeveloperEnabled(window.localStorage);
    } catch (err) {
      const message = failureReason(err);
      settingsWindowLogError('developer options persist failed', {
        reason: message,
      });
      setDeveloperEnabled(true);
      toast(t('settingsWindow.developer.persistFailed', { message }));
      return;
    }
    setDeveloperEnabled(true);
    toast(t('settingsWindow.developer.enabled'));
  }, [t]);

  const openAboutLink = useCallback(
    (url: string) => {
      if (openExternalBridge) {
        if (!openExternalBridge.open(url)) {
          settingsWindowLogWarn('settings about open blocked');
        }
        return;
      }
      if (launcherService) {
        void launcherService.openExternal(url).catch((err: unknown) => {
          settingsWindowLogError('settings about open failed', {
            reason: failureReason(err),
          });
        });
        return;
      }
      settingsWindowLogWarn('settings about open unavailable');
    },
    [openExternalBridge, launcherService],
  );

  const disableDeveloper = useCallback(() => {
    try {
      clearDeveloperEnabled(window.localStorage);
    } catch (err) {
      const message = failureReason(err);
      settingsWindowLogError('developer options persist failed', {
        reason: message,
      });
      toast(t('settingsWindow.developer.hideFailed', { message }));
    }
    developerTap.current = initialDeveloperTapState(false);
    setDeveloperEnabled(false);
    setPageId(SETTING_PAGE_ABOUT);
  }, [t]);

  const scrollerRef = useRef<HTMLDivElement>(null);
  const pendingScroll = useRef<string | null>(null);
  const spyLock = useRef(false);
  const [anchor, setAnchor] = useState<string | null>(null);

  const revealAnchors = useCallback(() => {
    const root = scrollerRef.current;
    if (!root) {
      return;
    }
    const next = readActiveAnchor(root);
    if (next) {
      setAnchor((prev) => (prev === next ? prev : next));
    }
  }, []);

  const scrollToAnchor = useCallback((id: string) => {
    const root = scrollerRef.current;
    if (!root) {
      return;
    }
    const target = root.querySelector(
      `[data-settings-anchor="${CSS.escape(id)}"]`,
    );
    if (
      !(target instanceof HTMLElement) ||
      target.getClientRects().length === 0
    ) {
      spyLock.current = false;
      return;
    }
    const delta =
      target.getBoundingClientRect().top -
      root.getBoundingClientRect().top -
      scrollerPadding(root).top;
    if (Math.abs(delta) < 2) {
      spyLock.current = false;
      return;
    }
    spyLock.current = true;
    let timer = 0;
    const unlock = () => {
      spyLock.current = false;
      window.clearTimeout(timer);
      root.removeEventListener('scrollend', unlock);
      const next = readActiveAnchor(root);
      if (next) {
        setAnchor((prev) => (prev === next ? prev : next));
      }
    };
    root.addEventListener('scrollend', unlock);
    timer = window.setTimeout(unlock, 800);
    root.scrollTo({
      top: root.scrollTop + delta,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }, []);

  const currentPageId = page?.id ?? null;

  const openPage = useCallback(
    (target: SettingsTreePage) => {
      if (target.id !== currentPageId) {
        leave(() => {
          setAnchor(null);
          setDrill(null);
          setPageId(target.id);
        });
        return;
      }
      setAnchor(null);
      scrollerRef.current?.scrollTo({
        top: 0,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    },
    [currentPageId, leave],
  );

  const showRequestedPage = useCallback(
    (requested: string) => {
      const target = findSettingsPage(tree, requested);
      if (!target) {
        leave(() => {
          setAnchor(null);
          setDrill(null);
          setPageId(requested);
        });
        return;
      }
      openPage(target);
    },
    [tree, leave, openPage],
  );

  useEffect(() => {
    if (!client) {
      return;
    }
    return client.listen(SETTINGS_WINDOW_SELECT_PAGE_EVENT, (data) => {
      const requested = readSettingsPageId(data);
      if (requested) {
        showRequestedPage(requested);
      }
    });
  }, [client, showRequestedPage]);

  const openAnchor = useCallback(
    (section: SettingsTreeSection) => {
      const host = settingsPageOfSection(tree, section.id);
      if (!host) {
        return;
      }
      if (host.id !== currentPageId) {
        leave(() => {
          setAnchor(section.id);
          setDrill(null);
          spyLock.current = true;
          pendingScroll.current = section.id;
          setPageId(host.id);
        });
        return;
      }
      setAnchor(section.id);
      scrollToAnchor(section.id);
    },
    [currentPageId, leave, scrollToAnchor, tree],
  );

  useEffect(() => {
    const root = scrollerRef.current;
    if (!root) {
      return;
    }
    const id = pendingScroll.current;
    if (id) {
      pendingScroll.current = null;
      const frame = requestAnimationFrame(() => scrollToAnchor(id));
      return () => cancelAnimationFrame(frame);
    }
    root.scrollTop = 0;
  }, [currentPageId, scrollToAnchor]);

  useEffect(() => {
    const root = scrollerRef.current;
    if (!root) {
      return;
    }
    const publish = () => {
      const padding = scrollerPadding(root);
      const height = Math.max(
        0,
        root.clientHeight - padding.top - padding.bottom,
      );
      root.style.setProperty(ANCHOR_LAST_MIN_HEIGHT_VAR, `${height}px`);
    };
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(root);
    return () => observer.disconnect();
  }, []);

  const drilling = drill !== null;
  useLayoutEffect(() => {
    if (drilling && scrollerRef.current) {
      scrollerRef.current.scrollTop = 0;
    }
  }, [drilling]);

  useEffect(() => {
    const root = scrollerRef.current;
    if (!root) {
      return;
    }
    let frame = 0;
    const update = () => {
      frame = 0;
      if (spyLock.current) {
        return;
      }
      const next = readActiveAnchor(root);
      if (!next) {
        return;
      }
      setAnchor((prev) => (prev === next ? prev : next));
    };
    const onScroll = () => {
      if (frame) {
        return;
      }
      frame = requestAnimationFrame(update);
    };
    root.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => {
      root.removeEventListener('scroll', onScroll);
      if (frame) {
        cancelAnimationFrame(frame);
      }
    };
  }, [currentPageId]);

  const runAction = useCallback(
    (action: SettingsTreeAction) => {
      leave(() => {
        void (async () => {
          try {
            await moduleService.executeCommand(action.command);
            if (action.closesWindow) {
              windowControls?.close();
            }
          } catch (err) {
            const message = failureReason(err);
            settingsWindowLogError('settings window action failed', {
              action: action.id,
              reason: message,
            });
            toast(t('settingsWindow.actionFailed', { message }));
          }
        })();
      });
    },
    [leave, moduleService, t, windowControls],
  );

  const requestAction = useCallback(
    (action: SettingsTreeAction) => {
      if (action.confirm) {
        setPendingAction(action);
        return;
      }
      runAction(action);
    },
    [runAction],
  );

  const setSectionDrill = useCallback(
    (sectionId: string, next: SettingsPaneDrill | null) => {
      setDrill((prev) => {
        if (next) {
          return { ...next, sectionId };
        }
        return prev?.sectionId === sectionId ? null : prev;
      });
    },
    [],
  );
  const setSectionGuard = useCallback(
    (sectionId: string, guard: SettingsLeaveGuard | null) => {
      if (guard) {
        leaveGuards.current.set(sectionId, guard);
      } else {
        leaveGuards.current.delete(sectionId);
      }
    },
    [],
  );

  const nativeChrome = windowControls?.chrome === 'native';
  const customChrome = windowControls?.chrome === 'custom';
  const heading = drill ? drill.title : (page?.title ?? '');
  const header =
    tree.header && renderModuleView ? (
      <SettingsSectionErrorBoundary fallback={null}>
        {renderModuleView(tree.header.moduleId, tree.header.viewId)}
      </SettingsSectionErrorBoundary>
    ) : null;

  const renderPageRow = (row: SettingsTreePage): ReactNode => {
    const selected = row.id === page?.id;
    const open = searching || selected;
    const currentAnchorIndex = selected
      ? row.anchors.findIndex((section) => section.id === anchor)
      : -1;
    return (
      <div key={row.id} className="flex w-full flex-col">
        <button
          type="button"
          className={cn(
            'group flex w-full items-center rounded-[14px] p-3 text-left',
            selected ? SIDEBAR_SELECTED : SIDEBAR_PRESS,
          )}
          onClick={() => openPage(row)}
        >
          <NavGlyph spec={row.icon} selected={selected} />
          <span
            className={cn(
              'min-w-0 truncate text-[14px] leading-[22px]',
              selected
                ? 'font-medium text-(--app-color-text-primary)'
                : 'text-(--app-color-text-secondary) group-hover:font-medium group-hover:text-(--app-color-text-primary)',
            )}
          >
            {row.title}
          </span>
        </button>
        {row.anchors.length > 0 ? (
          <div
            className="settings-nav-accordion"
            data-open={open ? 'true' : 'false'}
            aria-hidden={open ? undefined : true}
            inert={open ? undefined : true}
          >
            <div className="settings-nav-accordion-inner relative">
              {/* Figma `9229:191446`: gray00 4% rail, 1.4px, left 21.3. */}
              <span
                aria-hidden
                className="pointer-events-none absolute top-[13px] bottom-[13px] left-[21.3px] z-0 w-[1.4px] rounded-full bg-(--app-base-black-a4) dark:bg-(--app-base-white-a4)"
              />
              {/* Figma `9229:191445` selection bar: Color/text/primary, 16×1.4.
                  One tick per page so it slides between anchors. */}
              {selected && currentAnchorIndex >= 0 ? (
                <span
                  aria-hidden
                  className="settings-nav-tick pointer-events-none absolute top-0 left-[21.3px] z-[1] h-4 w-[1.4px] rounded-full bg-(--app-color-text-primary) motion-reduce:transition-none"
                  style={{
                    transform: `translateY(${currentAnchorIndex * ANCHOR_ROW_HEIGHT + ANCHOR_TICK_TOP}px)`,
                  }}
                />
              ) : null}
              {row.anchors.map((section) => {
                const current = selected && anchor === section.id;
                return (
                  <button
                    key={section.id}
                    type="button"
                    className={cn(
                      'group flex w-full items-center rounded-[14px] py-2.5 text-left',
                      SIDEBAR_PRESS,
                    )}
                    onClick={() => openAnchor(section)}
                  >
                    <span className="w-10 shrink-0" />
                    <span
                      className={cn(
                        'min-w-0 truncate text-[14px] leading-[22px]',
                        current
                          ? 'text-(--app-color-text-primary)'
                          : 'text-(--app-color-text-tertiary) group-hover:text-(--app-color-text-primary)',
                      )}
                    >
                      {section.title}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderActionRow = (action: SettingsTreeAction): ReactNode => {
    const tone = action.danger
      ? 'text-(--app-color-text-error)'
      : 'text-(--app-color-text-secondary)';
    return (
      <button
        key={action.id}
        type="button"
        className={cn(
          'flex w-full items-center gap-2 rounded-[14px] p-3 text-left',
          SIDEBAR_PRESS,
        )}
        onClick={() => requestAction(action)}
      >
        {action.icon ? (
          <Icon spec={action.icon} size={20} className={cn('size-5', tone)} />
        ) : null}
        <span className={cn('text-[14px] leading-[22px]', tone)}>
          {action.title}
        </span>
      </button>
    );
  };

  return (
    <div className="relative flex h-full w-full bg-(--app-color-bg-module-base) p-1.5">
      <aside className="flex h-full w-[256px] shrink-0 flex-col gap-4 overflow-hidden rounded-[10px] bg-(--app-color-bg-module-container)">
        <div
          className="flex shrink-0 items-center p-1.5"
          style={{
            minHeight: nativeChrome ? MAC_TRAFFIC_LIGHT_SPACING : 6,
          }}
        />
        <div className="flex min-h-0 flex-1 flex-col gap-2.5 px-5 pb-1.5">
          <div className="flex shrink-0 flex-col gap-3.5">
            {header}
            <label className="flex h-11 w-full items-center gap-2 rounded-[14px] border border-(--app-color-border) bg-(--app-color-card) py-2.5 pr-2.5 pl-3">
              <Icon
                spec={{ type: 'builtin', id: 'i-search' }}
                size={20}
                className="size-5 text-(--app-color-text-tertiary)"
              />
              <input
                value={query}
                placeholder={t('settingsWindow.search')}
                onChange={(event) => setQuery(event.target.value)}
                className="min-w-0 flex-1 bg-transparent text-[14px] leading-[22px] text-(--app-color-text-primary) outline-none placeholder:text-(--app-color-text-tertiary)"
              />
            </label>
          </div>
          {/* Gutter compensation: see `.app-settings-sidebar-scroll`. */}
          <nav className="app-settings-sidebar-scroll flex min-h-0 flex-1 flex-col overflow-y-auto">
            {shown.groups.map((group, index) => (
              <div key={group.id} className="flex w-full flex-col">
                {index > 0 ? <div className={DIVIDER} /> : null}
                {group.title ? (
                  <p className="px-3 pt-2.5 pb-1 text-[13px] leading-5 text-(--app-color-text-tertiary)">
                    {group.title}
                  </p>
                ) : null}
                {group.pages.length > 0 ? (
                  <div
                    className={cn(
                      'flex w-full flex-col',
                      index > 0 && !group.title ? 'py-1.5' : 'pb-1.5',
                    )}
                  >
                    {group.pages.map(renderPageRow)}
                  </div>
                ) : null}
                {group.actions.length > 0 ? (
                  <>
                    <div className={DIVIDER} />
                    <div className="flex w-full flex-col pb-1.5">
                      {group.actions.map(renderActionRow)}
                    </div>
                  </>
                ) : null}
              </div>
            ))}
          </nav>
        </div>
      </aside>

      <section className="flex min-w-0 flex-1 flex-col">
        <header className="settings-titlebar relative flex h-14 shrink-0 items-center justify-center px-5 pt-2.5">
          {/* Caption buttons are absolute, so a right-only padding would
            shift `text-center` left. Inset both sides by the same amount. */}
          <h1
            className={cn(
              'pointer-events-none absolute inset-x-0 top-2.5 bottom-0 flex items-center justify-center text-[16px] leading-6 font-medium text-(--app-color-text-primary)',
              customChrome ? 'px-[136px]' : 'px-16',
            )}
          >
            <span className="truncate">{heading}</span>
          </h1>
          <div
            className="absolute left-3 flex items-center gap-2"
            style={{ WebkitAppRegion: 'no-drag' }}
          >
            {drill ? (
              <button
                type="button"
                className="flex size-11 cursor-pointer items-center justify-center rounded-full bg-(--app-color-card) shadow-[0_2px_6px_0_rgba(0,0,0,0.04)] hover:shadow-[0_2px_6px_0_rgba(0,0,0,0.04),inset_0_0_0_999px_var(--app-color-card-active)] focus-visible:outline-2 focus-visible:outline-(--app-base-brand) active:shadow-[0_2px_6px_0_rgba(0,0,0,0.04),inset_0_0_0_999px_var(--app-base-black-a10)] dark:active:shadow-[0_2px_6px_0_rgba(0,0,0,0.04),inset_0_0_0_999px_var(--app-base-white-a30)]"
                aria-label={t('settingsWindow.account.back')}
                onClick={() => drill.onBack()}
              >
                <Icon
                  spec={{ type: 'builtin', id: 'i-chevron-left' }}
                  size={24}
                  className="size-6 text-(--app-color-text-primary)"
                />
              </button>
            ) : null}
          </div>
          {customChrome && windowControls ? (
            <div className="absolute top-0 right-0">
              <WindowControls bridge={windowControls} />
            </div>
          ) : null}
        </header>
        <div
          ref={scrollerRef}
          className="flex min-h-0 flex-1 flex-col overflow-y-auto pt-6 pb-6"
        >
          {page ? (
            <PageViewLoadingProvider key={page.id}>
              {(waiting) => (
                <SettingsPagePane
                  waiting={waiting}
                  label={t('surface.view.loading')}
                  onReveal={revealAnchors}
                >
                  {page.id === SETTING_PAGE_NOTIFICATIONS ? (
                    <NotificationsSettings
                      sections={page.sections}
                      tree={tree}
                      config={config}
                      label={title}
                      t={t}
                    />
                  ) : null}
                  {page.sections
                    .filter(
                      (section) =>
                        page.id !== SETTING_PAGE_NOTIFICATIONS ||
                        (section.body.kind !== 'keys' &&
                          section.body.kind !== 'builtin'),
                    )
                    .map((section, index, visible) => (
                      <SectionHost
                        key={section.id}
                        section={section}
                        last={index === visible.length - 1}
                        hidden={
                          drill !== null && drill.sectionId !== section.id
                        }
                        overlayRoot={overlayRoot}
                        onDrill={setSectionDrill}
                        onGuard={setSectionGuard}
                      >
                        <SectionBody
                          section={section}
                          t={t}
                          title={title}
                          config={config}
                          renderModuleView={renderModuleView}
                          moduleService={moduleService}
                          launcherService={launcherService}
                          client={client}
                          nlsService={nlsService}
                          themeService={themeService}
                          updateService={updateService}
                          productConfigService={productConfigService}
                          whenContextService={whenContextService}
                          openAboutLink={openAboutLink}
                          onAboutLogoTap={onAboutLogoTap}
                          developerSettings={developerSettings}
                          onDisableDeveloper={disableDeveloper}
                        />
                      </SectionHost>
                    ))}
                </SettingsPagePane>
              )}
            </PageViewLoadingProvider>
          ) : null}
        </div>
      </section>
      {/* Section sheets portal here and cover the whole window (`absolute inset-0`). */}
      <div ref={setOverlayRoot} />
      {pendingAction?.confirm ? (
        <SettingsPaneChromeProvider
          setDrill={ignorePaneDrill}
          setLeaveGuard={ignoreLeaveGuard}
          overlayRoot={overlayRoot}
        >
          <SettingsConfirmSheet
            title={pendingAction.confirm.title}
            body={pendingAction.confirm.body}
            confirm={pendingAction.confirm.confirm}
            cancelLabel={t('settingsWindow.overlay.cancel')}
            closeLabel={t('settingsWindow.overlay.close')}
            danger={pendingAction.danger}
            onConfirm={() => runAction(pendingAction)}
            onClose={() => setPendingAction(null)}
          />
        </SettingsPaneChromeProvider>
      ) : null}
    </div>
  );
}

/**
 * One page's stack. While any inline view on it is still activating, the pane
 * shows a single spinner and the sections stay mounted without taking space.
 * When that wait ends, the scroll spy can see the anchors again.
 */
function SettingsPagePane({
  waiting,
  label,
  onReveal,
  children,
}: {
  waiting: boolean;
  label: string;
  onReveal: () => void;
  children: ReactNode;
}): ReactNode {
  const wasWaiting = useRef(false);
  useEffect(() => {
    if (wasWaiting.current && !waiting) {
      onReveal();
    }
    wasWaiting.current = waiting;
  }, [onReveal, waiting]);
  return (
    <div
      className={
        waiting
          ? 'flex min-h-0 w-full flex-1 flex-col'
          : 'flex w-full flex-col gap-6'
      }
    >
      {waiting ? <SurfaceLoadingIndicator label={label} /> : null}
      <div
        hidden={waiting ? true : undefined}
        className={waiting ? undefined : 'contents'}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * One section's anchor and its own pane-chrome channel: its drill and leave
 * guard are keyed by the section, and dropped when the section unmounts.
 */
function SectionHost({
  section,
  last,
  hidden,
  overlayRoot,
  onDrill,
  onGuard,
  children,
}: {
  section: SettingsTreeSection;
  last: boolean;
  hidden: boolean;
  overlayRoot: HTMLElement | null;
  onDrill: (sectionId: string, drill: SettingsPaneDrill | null) => void;
  onGuard: (sectionId: string, guard: SettingsLeaveGuard | null) => void;
  children: ReactNode;
}): ReactNode {
  const { id } = section;
  const setDrill = useCallback(
    (next: SettingsPaneDrill | null) => onDrill(id, next),
    [id, onDrill],
  );
  const setLeaveGuard = useCallback(
    (guard: SettingsLeaveGuard | null) => onGuard(id, guard),
    [id, onGuard],
  );
  useEffect(
    () => () => {
      onDrill(id, null);
      onGuard(id, null);
    },
    [id, onDrill, onGuard],
  );
  return (
    <SettingsPaneChromeProvider
      setDrill={setDrill}
      setLeaveGuard={setLeaveGuard}
      overlayRoot={overlayRoot}
    >
      {/* Hidden, not unmounted, so a sibling section survives a drill round-trip. */}
      <AnchorSection id={id} last={last} hidden={hidden}>
        {children}
      </AnchorSection>
    </SettingsPaneChromeProvider>
  );
}

function SectionBody({
  section,
  t,
  title,
  config,
  renderModuleView,
  moduleService,
  launcherService,
  whenContextService,
  ...builtin
}: {
  section: SettingsTreeSection;
  t: (key: string, args?: Record<string, unknown>) => string;
  title: (moduleId: string, raw: string) => string;
  config: SettingsConfig;
  renderModuleView?: (moduleId: string, viewId: string) => ReactNode;
  moduleService: IModuleService;
  launcherService?: ILauncherService;
  whenContextService?: IWhenContextService;
  client?: ChannelClient;
  nlsService?: INlsService;
  themeService?: IThemeService;
  updateService?: IUpdateService;
  productConfigService?: IProductConfigService;
  openAboutLink: (url: string) => void;
  onAboutLogoTap: () => void;
  developerSettings?: ReactNode;
  onDisableDeveloper: () => void;
}): ReactNode {
  const { body } = section;
  switch (body.kind) {
    case 'builtin':
      return (
        <BuiltinSection
          builtin={body.builtin}
          t={t}
          moduleService={moduleService}
          launcherService={launcherService}
          whenContextService={whenContextService}
          {...builtin}
        />
      );
    case 'keys':
      return (
        <SettingsPane>
          <KeysSection
            title={section.title}
            keys={body.keys}
            config={config}
            label={title}
            t={t}
          />
        </SettingsPane>
      );
    case 'view': {
      const view = renderModuleView?.(body.moduleId, body.viewId);
      return (
        <SettingsSectionErrorBoundary fallback={null}>
          {view ?? (
            <UnavailableView
              sectionId={section.id}
              viewId={body.viewId}
              t={t}
            />
          )}
        </SettingsSectionErrorBoundary>
      );
    }
  }
}

function UnavailableView({
  sectionId,
  viewId,
  t,
}: {
  sectionId: string;
  viewId: string;
  t: (key: string) => string;
}): ReactNode {
  useEffect(() => {
    settingsWindowLogWarn('settings window section view unavailable', {
      section: sectionId,
      viewId,
    });
  }, [sectionId, viewId]);
  return (
    <SettingsPane>
      <SettingsHint>{t('settingsWindow.unavailable')}</SettingsHint>
    </SettingsPane>
  );
}

function NavGlyph({
  spec,
  selected,
}: {
  spec: IconSpec | undefined;
  selected: boolean;
}): ReactNode {
  const color = selected
    ? 'text-(--app-color-accent)'
    : 'text-(--app-color-text-secondary) group-hover:text-(--app-color-text-primary)';
  if (!spec) {
    return <span aria-hidden className="mr-2 size-5 shrink-0" />;
  }
  if (spec.type === 'builtin' && spec.id === 'i-global') {
    return <LanguageIcon className={cn('mr-2 size-5', color)} />;
  }
  return <Icon spec={spec} size={20} className={cn('mr-2 size-5', color)} />;
}
